/**
 * EvidenceLedger — the **evidence ledger** Relations surface for a Claim
 * (frontend-entity-coverage §2: "evidence ledger (supporting/contradicting,
 * polarity × weight)"; the defining Claim surface, `02 §3.4`).
 *
 * Fetches the evidence whose `claim_id` equals this claim (the boxed
 * dataProvider → `GET /api/evidence?claim_id=…`) and groups it by polarity
 * stance — **supporting** (`supports` / `qualifies`) vs **contradicting**
 * (`contradicts` / `weakens`) — surfacing each row's polarity × weight, the
 * inputs to the claim-verification gate (`03 §12`). Each row is an Inspector
 * RefLink to the evidence show page.
 *
 * Net pull is shown as a compact weighted tally (Σ supporting weight − Σ
 * contradicting weight) so an operator reads the balance at a glance. Degrades:
 * a missing `evidence` list endpoint renders the calm empty note, never a crash.
 */
import * as React from "react";
import { useList } from "@refinedev/core";
import { FileText } from "@/components/ui/icon";

import type { Evidence, EvidencePolarity } from "@aos/shared";
import { Skeleton } from "@/components/ui/skeleton";
import { RefLink } from "@/components/inspector/RefLink";

import { PolarityBadge } from "../evidence/_polarity";

/** Whether a polarity stance counts as supporting (vs contradicting) the claim. */
const SUPPORTING: Record<EvidencePolarity, boolean> = {
  supports: true,
  qualifies: true,
  contradicts: false,
  weakens: false,
};

function weightOf(e: Evidence): number {
  return typeof e.weight === "number" ? e.weight : 1;
}

function EvidenceRow({ e }: { e: Evidence }) {
  return (
    <div className="flex h-9 items-center gap-2 rounded-md px-2 hover:bg-accent">
      <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      <span className="min-w-0 truncate text-sm">
        <RefLink resource="evidence" id={e.id} label={e.title} />
      </span>
      <span className="ml-auto flex shrink-0 items-center gap-2">
        <PolarityBadge polarity={e.polarity} />
        <span className="font-mono text-xs text-muted-foreground">
          ×{weightOf(e).toFixed(2)}
        </span>
      </span>
    </div>
  );
}

function Stance({
  title,
  rows,
  emptyMessage,
}: {
  title: string;
  rows: Evidence[];
  emptyMessage: string;
}) {
  return (
    <div className="space-y-2">
      <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {title} ({rows.length})
      </h4>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
      ) : (
        <div>
          {rows.map((e) => (
            <EvidenceRow key={e.id} e={e} />
          ))}
        </div>
      )}
    </div>
  );
}

export interface EvidenceLedgerProps {
  claimId: string;
}

/** The polarity-grouped evidence ledger for a claim, with a weighted net tally. */
export function EvidenceLedger({ claimId }: EvidenceLedgerProps) {
  const { data, isLoading, isError } = useList<Evidence>({
    resource: "evidence",
    filters: [{ field: "claim_id", operator: "eq", value: claimId }],
    pagination: { pageSize: 100, mode: "server" },
    sorters: [{ field: "created_at", order: "desc" }],
    queryOptions: { retry: false },
    successNotification: false,
    errorNotification: false,
  });

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-3/4" />
      </div>
    );
  }

  // A missing/forbidden evidence list degrades to the empty ledger note.
  const rows = isError ? [] : data?.data ?? [];
  const supporting = rows.filter((e) => SUPPORTING[e.polarity]);
  const contradicting = rows.filter((e) => !SUPPORTING[e.polarity]);

  const supW = supporting.reduce((acc, e) => acc + weightOf(e), 0);
  const conW = contradicting.reduce((acc, e) => acc + weightOf(e), 0);
  const net = supW - conW;

  return (
    <div className="space-y-5" data-testid="claim-evidence-ledger">
      {rows.length > 0 ? (
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs">
          <span className="text-muted-foreground">
            Supporting weight{" "}
            <span className="font-mono text-foreground">{supW.toFixed(2)}</span>
          </span>
          <span className="text-muted-foreground">
            Contradicting weight{" "}
            <span className="font-mono text-foreground">{conW.toFixed(2)}</span>
          </span>
          <span className="text-muted-foreground">
            Net{" "}
            <span
              className={
                net > 0
                  ? "font-mono text-success"
                  : net < 0
                    ? "font-mono text-destructive"
                    : "font-mono text-foreground"
              }
            >
              {net >= 0 ? "+" : ""}
              {net.toFixed(2)}
            </span>
          </span>
        </div>
      ) : null}

      <Stance
        title="Supporting"
        rows={supporting}
        emptyMessage="No supporting evidence yet."
      />
      <Stance
        title="Contradicting"
        rows={contradicting}
        emptyMessage="No contradicting evidence yet."
      />
    </div>
  );
}

export default EvidenceLedger;
